import React, { useEffect, useState } from 'react';
import { API } from '../config';
import styles from './AdminPermissions.module.css';

const AdminPermissions = () => {
  const [students, setStudents] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchPermissions();
    fetchPages();
  }, []);

  const fetchPermissions = async () => {
    try {
      const res = await fetch(`${API.BASE}/get-all-student-permissions.php`);
      const data = await res.json();
      if (data.success) setStudents(data.students);
    } catch (err) {
      console.error("Error loading students", err);
    }
  };

  const fetchPages = async () => {
    try {
      const res = await fetch(API.ALL_PAGES);
      const data = await res.json();
      if (data.success) setAllPages(data.pages);
    } catch (err) {
      console.error("Failed to fetch pages", err);
    }
  };

  const togglePage = (studentId, pageId) => {
    setStudents(prev =>
      prev.map(s => {
        if (s.id !== studentId) return s;

        const pageExists = s.assigned_pages.some(p => String(p.id) === String(pageId));
        const updatedPages = pageExists
          ? s.assigned_pages.filter(p => String(p.id) !== String(pageId))
          : [...s.assigned_pages, allPages.find(p => String(p.id) === String(pageId))];

        return { ...s, assigned_pages: updatedPages };
      })
    );
  };

  const toggleAccess = (studentId) => {
    setStudents(prev =>
      prev.map(s =>
        s.id === studentId ? { ...s, access: s.access ? 0 : 1 } : s
      )
    );
  };

  const handleSave = async (student) => {
    setLoading(true);
    try {
      const res = await fetch(API.GRANT_ACCESS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: student.id,
          access: student.access,
          page_ids: student.assigned_pages.map(p => p.id),
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert(`Permissions updated for ${student.name}`);
        setEditId(null);
      } else {
        alert(data.message || "Failed to update");
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    }
    setLoading(false);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Student Page Permissions</h2>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name & Email</th>
              <th>Course & Batch</th>
              <th>Assigned Pages</th>
              <th>Access</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map(student => {
              const isEditing = editId === student.id;
              return (
                <tr key={student.id}>
                  <td>
                    <strong>{student.name}</strong><br />
                    <span className={styles.email}>{student.email}</span>
                  </td>
                  <td>
                    {student.coursename} <br />
                    <span className={styles.batch}>{student.batch_name}</span>
                  </td>
                  <td>
                    {isEditing ? (
                      <div className={styles.checkboxGroup}>
                        {allPages.map(page => {
                          const isChecked = student.assigned_pages.some(
                            p => String(p.id) === String(page.id)
                          );
                          return (
                            <label key={page.id} className={styles.checkboxLabel}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePage(student.id, page.id)}
                              />
                              {page.title}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <ul className={styles.pageList}>
                        {student.assigned_pages.map(p => (
                          <li key={p.id}>{p.title}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={student.access === 1}
                        onChange={() => toggleAccess(student.id)}
                      />
                    ) : (
                      student.access ? '✅' : '❌'
                    )}
                  </td>
                  <td>
                    {!isEditing ? (
                      <button onClick={() => setEditId(student.id)} className={styles.editBtn}>Edit</button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSave(student)}
                          className={styles.saveBtn}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          className={styles.cancelBtn}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPermissions;
